import {useQuery} from "@tanstack/react-query";
import {AxiosError} from "axios";
import {createBookingScheduleRepository} from "../data/repositories";
import {BookingSchedule, UnavailableDateRange} from "../domain/types";
import {Weekday} from "../domain/weekdays";

export const BOOKING_AVAILABLE_DAYS_QUERY_KEY = "booking_available_days";
export const BOOKING_UNAVAILABLE_DATES_QUERY_KEY = "booking_unavailable_dates";

/**
 * A coach's schedule changes rarely and the reschedule drawer is opened and
 * closed repeatedly, so hold it for five minutes rather than refetching on every
 * open.
 */
const SCHEDULE_STALE_TIME_MS = 5 * 60 * 1000;

type ApiError = AxiosError<{status: boolean; message: string}>;

export type UseBookingScheduleParams = {
  mentorId: number;
  serviceId: number;
  /**
   * Formerly the v1 fallback (v1 embedded the schedule in the booking payload
   * instead of exposing standalone endpoints). v1 is gone and the schedule
   * repository always resolves via v2 now, so `days` is no longer read; the
   * field is kept so existing callers don't need to change.
   */
  fallback: {
    days?: unknown;
    unavailableDates?: UnavailableDateRange[] | null;
  };
  enabled?: boolean;
};

export type UseBookingScheduleResult = BookingSchedule & {
  isLoading: boolean;
  isError: boolean;
  error: ApiError | null;
};

/**
 * View-logic hook: resolves the weekdays a coach takes bookings on and the date
 * ranges they have blocked out, for the booking calendar grid.
 */
export const useBookingSchedule = ({
  mentorId,
  serviceId,
  enabled = true,
}: UseBookingScheduleParams): UseBookingScheduleResult => {
  const repository = createBookingScheduleRepository();
  const canQuery = enabled && !!mentorId && !!serviceId;

  const availableWeekdaysQuery = useQuery<Weekday[], ApiError>({
    queryKey: [BOOKING_AVAILABLE_DAYS_QUERY_KEY, mentorId, serviceId],
    queryFn: () => repository.getAvailableWeekdays({mentorId, serviceId}),
    enabled: canQuery,
    staleTime: SCHEDULE_STALE_TIME_MS,
  });

  const unavailableDatesQuery = useQuery<UnavailableDateRange[], ApiError>({
    queryKey: [BOOKING_UNAVAILABLE_DATES_QUERY_KEY, mentorId],
    queryFn: () => repository.getUnavailableDates({mentorId}),
    enabled: canQuery,
    staleTime: SCHEDULE_STALE_TIME_MS,
  });

  return {
    availableWeekdays: availableWeekdaysQuery.data ?? [],
    unavailableDates: unavailableDatesQuery.data ?? [],
    // isInitialLoading, not isLoading: a disabled query reports isLoading true
    // forever, which would leave the calendar stuck behind a spinner.
    isLoading:
      availableWeekdaysQuery.isInitialLoading || unavailableDatesQuery.isInitialLoading,
    isError: availableWeekdaysQuery.isError || unavailableDatesQuery.isError,
    error: availableWeekdaysQuery.error ?? unavailableDatesQuery.error ?? null,
  };
};
